/**
 * Форматирование.
 *
 * Управленческие суммы — сотни миллионов, и полное число в таблице
 * не читается. В таблицах показываем разряды целиком (их сравнивают
 * глазами по столбцу), в плитках и на осях — сокращённо.
 */

import type { ColumnType } from './types'

const nf = (options: Intl.NumberFormatOptions): Intl.NumberFormat =>
  new Intl.NumberFormat('ru-RU', options)

const money0 = nf({ maximumFractionDigits: 0 })
const money2 = nf({ minimumFractionDigits: 2, maximumFractionDigits: 2 })
const plain = nf({ maximumFractionDigits: 2 })

export function formatMoney(value: unknown, exact = false): string {
  const number = toNumber(value)
  if (number === null) return '—'
  return exact ? money2.format(number) : money0.format(number)
}

/** Сокращённая сумма для плиток и подписей осей: 873 млн, 12,5 млн, 480 тыс. */
export function formatMoneyShort(value: unknown): string {
  const number = toNumber(value)
  if (number === null) return '—'

  const abs = Math.abs(number)
  if (abs >= 1_000_000_000) return `${plain.format(round(number / 1_000_000_000, 2))} млрд`
  if (abs >= 1_000_000) return `${plain.format(round(number / 1_000_000, abs >= 10_000_000 ? 0 : 1))} млн`
  if (abs >= 10_000) return `${plain.format(round(number / 1000, 0))} тыс.`
  return money0.format(number)
}

/** Сокращённая сумма со знаком рубля: «15 млн ₽». Прочерк — без знака. */
export function formatRub(value: unknown): string {
  const text = formatMoneyShort(value)
  return text === '—' ? text : `${text} ₽`
}

export function formatNumber(value: unknown): string {
  const number = toNumber(value)
  return number === null ? '—' : plain.format(number)
}

export function formatPercent(value: unknown): string {
  const number = toNumber(value)
  return number === null ? '—' : `${plain.format(round(number, 1))}%`
}

/**
 * Изменение к прошлому периоду в процентах.
 *
 * null означает «сравнить не с чем»: прошлого значения нет или оно нулевое.
 * Рост с нуля — это не «+∞ процентов», а отсутствие базы для сравнения,
 * и показывать его процентом нечестно.
 */
export function deltaPercent(current: unknown, previous: unknown): number | null {
  const now = toNumber(current)
  const before = toNumber(previous)
  if (now === null || before === null || before === 0) return null
  return Number((((now - before) / Math.abs(before)) * 100).toFixed(1))
}

export function formatDelta(value: unknown): string {
  const number = toNumber(value)
  if (number === null) return '—'
  const sign = number > 0 ? '+' : number < 0 ? '−' : ''
  return `${sign}${plain.format(Math.abs(round(number, 1)))}%`
}

/**
 * Изменение процентного показателя измеряется в процентных пунктах.
 *
 * Рентабельность выросла с 26,0% до 26,3% — это +0,3 п.п., а не +1,2%.
 * Второе формально верно как «процент от процента», но читается как рост
 * рентабельности на процент и вводит в заблуждение.
 */
export function deltaPoints(current: unknown, previous: unknown): number | null {
  const now = toNumber(current)
  const before = toNumber(previous)
  if (now === null || before === null) return null
  return Number((now - before).toFixed(2))
}

export function formatDeltaPoints(value: unknown): string {
  const number = toNumber(value)
  if (number === null) return '—'
  const sign = number > 0 ? '+' : number < 0 ? '−' : ''
  return `${sign}${plain.format(Math.abs(round(number, 2)))} п.п.`
}

export function formatCell(value: unknown, type: ColumnType): string {
  if (type === 'delta') return formatDelta(value)
  if (type === 'delta_pp') return formatDeltaPoints(value)
  if (value === null || value === undefined || value === '') return '—'
  switch (type) {
    case 'money':
      return formatMoney(value)
    case 'number':
      return formatNumber(value)
    case 'percent':
      return formatPercent(value)
    case 'date':
      return formatMonth(String(value))
    default:
      return String(value)
  }
}

/** '2026-06-01' -> 'июнь 2026'. Периоды в витринах помесячные. */
export function formatMonth(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const months = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
  ]
  const month = months[Number(value.slice(5, 7)) - 1] ?? value.slice(5, 7)
  return `${month} ${value.slice(0, 4)}`
}

export function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** «5 минут назад», «3 часа назад» — для показа актуальности данных. */
export function formatRelative(value: string | null): string {
  if (!value) return 'нет данных'
  const diffMs = Date.now() - new Date(value).getTime()
  if (Number.isNaN(diffMs)) return value

  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')} назад`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ${plural(hours, 'час', 'часа', 'часов')} назад`

  const days = Math.round(hours / 24)
  if (days < 31) return `${days} ${plural(days, 'день', 'дня', 'дней')} назад`

  return formatDate(value)
}

export function plural(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(count) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return many
  if (last > 1 && last < 5) return few
  if (last === 1) return one
  return many
}

export function pluralize(count: number, one: string, few: string, many: string): string {
  return `${plain.format(count)} ${plural(count, one, few, many)}`
}

export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) ? number : null
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

/** Инициалы для аватара: «Смирнов Андрей» -> «СА». */
export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Первое число текущего месяца минус N месяцев, в формате YYYY-MM-DD. */
export function monthsAgo(count: number): string {
  const now = new Date()
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth() - count, 1))
  return date.toISOString().slice(0, 10)
}

export function endOfCurrentMonth(): string {
  const now = new Date()
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0))
  return date.toISOString().slice(0, 10)
}
